import { fetchConfig } from '@theme/utilities';
import { CartAddEvent, CartErrorEvent } from '@theme/events';

const SELECTORS = {
  root: '[data-bundle-offer]',
  variantsJson: '[data-bundle-offer-variants]',
  optionRadio: 'input[type="radio"][name^="bundle-offer-option-"]',
  picks: '[data-bundle-offer-picks]',
  row: '[data-bundle-offer-row]',
  freebie: '[data-bundle-offer-freebie]',
  atc: '[data-bundle-offer-atc]',
  error: '[data-bundle-offer-error]',
};

const QTY_LIMITS = {
  single: { min: 1, max: 5, fallback: 1, dataKey: 'bundleOfferSingleQty' },
  bundle2: { min: 2, max: 5, fallback: 2, dataKey: 'bundleOfferBundle2Qty' },
  bundle: { min: 2, max: 5, fallback: 3, dataKey: 'bundleOfferBundleQty' },
};

function safeJsonParse(text) {
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
}

function getCartSectionsToRender() {
  const ids = [];
  document.querySelectorAll('cart-items-component').forEach((el) => {
    if (el instanceof HTMLElement && el.dataset.sectionId) ids.push(el.dataset.sectionId);
  });
  return ids;
}

function findVariantId(variants, optionValues) {
  if (!Array.isArray(variants)) return null;
  const match = variants.find((v) => {
    if (!v) return false;
    if (Array.isArray(v.options)) {
      return v.options.every((val, idx) => (optionValues[idx] ?? null) === val);
    }
    // Fallback (option1/2/3)
    if (optionValues.length >= 1 && v.option1 !== optionValues[0]) return false;
    if (optionValues.length >= 2 && v.option2 !== optionValues[1]) return false;
    if (optionValues.length >= 3 && v.option3 !== optionValues[2]) return false;
    return true;
  });

  if (!match) return null;
  if (match.available === false) return null;
  return match.id ?? null;
}

function setError(root, message) {
  const el = root.querySelector(SELECTORS.error);
  if (!el) return;
  if (!message) {
    el.classList.add('hidden');
    el.textContent = '';
    return;
  }
  el.textContent = message;
  el.classList.remove('hidden');
}

function getMode(root) {
  const checked = root.querySelector(`${SELECTORS.optionRadio}:checked`);
  const value = checked?.value;
  if (value === 'single') return 'single';
  if (value === 'bundle2') return 'bundle2';
  return 'bundle';
}

function getQty(root, mode) {
  const cfg = QTY_LIMITS[mode] || QTY_LIMITS.bundle;
  const raw = root?.dataset?.[cfg.dataKey] ?? '';
  const parsed = Number.parseInt(String(raw), 10);
  const qty = Number.isFinite(parsed) ? parsed : cfg.fallback;
  return Math.max(cfg.min, Math.min(cfg.max, qty));
}

function syncSelectedOptionUI(root) {
  const radios = Array.from(root.querySelectorAll(SELECTORS.optionRadio));
  radios.forEach((radio) => {
    const label = radio.closest('label');
    if (!(label instanceof HTMLElement)) return;
    label.classList.toggle('is-selected', radio.checked);
  });
}

function getRowsByMode(root, mode) {
  const rows = Array.from(root.querySelectorAll(`${SELECTORS.row}[data-mode="${mode}"]`));
  rows.sort((a, b) => {
    const ai = Number.parseInt(a.getAttribute('data-row-index') || '1', 10) || 1;
    const bi = Number.parseInt(b.getAttribute('data-row-index') || '1', 10) || 1;
    return ai - bi;
  });
  return rows;
}

function setModeUI(root, mode) {
  const activeQty = getQty(root, mode);

  const picks = Array.from(root.querySelectorAll(SELECTORS.picks));
  picks.forEach((el) => {
    if (!(el instanceof HTMLElement)) return;
    const elMode = el.getAttribute('data-mode') || 'bundle';
    const collapsed = elMode !== mode;
    el.classList.toggle('is-collapsed', collapsed);
    el.toggleAttribute('inert', collapsed);
    el.setAttribute('aria-hidden', collapsed ? 'true' : 'false');
  });

  const activeRows = getRowsByMode(root, mode);
  const allRows = Array.from(root.querySelectorAll(SELECTORS.row));

  activeRows.forEach((row) => {
    const idx = Number.parseInt(row.getAttribute('data-row-index') || '1', 10) || 1;
    const shouldShow = idx <= activeQty;
    const collapsed = !shouldShow;
    row.classList.toggle('is-collapsed', collapsed);
    row.toggleAttribute('inert', collapsed);
    row.setAttribute('aria-hidden', collapsed ? 'true' : 'false');
  });

  allRows.forEach((row) => {
    const rowMode = row.getAttribute('data-mode') || 'bundle';
    if (rowMode === mode) return;
    row.classList.add('is-collapsed');
    row.toggleAttribute('inert', true);
    row.setAttribute('aria-hidden', 'true');
  });

  const freebie = root.querySelector(SELECTORS.freebie);
  if (freebie instanceof HTMLElement) {
    freebie.classList.toggle('is-collapsed', mode !== 'bundle');
  }
}

function getRowOptionValues(rowEl) {
  const values = [];
  const controls = Array.from(rowEl.querySelectorAll('[data-bundle-offer-option]'));
  controls.forEach((control) => {
    if (!(control instanceof HTMLElement)) return;
    const idx = Number.parseInt(control.getAttribute('data-option-index') || '0', 10) || 0;

    if (control instanceof HTMLSelectElement) {
      values[idx] = control.value;
      return;
    }

    if (control instanceof HTMLInputElement && control.type === 'radio') {
      if (control.checked) values[idx] = control.value;
    }
  });
  return values;
}

function setRowOptionValues(rowEl, optionValues) {
  const controls = Array.from(rowEl.querySelectorAll('[data-bundle-offer-option]'));
  controls.forEach((control) => {
    if (!(control instanceof HTMLElement)) return;
    const idx = Number.parseInt(control.getAttribute('data-option-index') || '0', 10) || 0;
    const value = optionValues[idx];
    if (value == null) return;

    if (control instanceof HTMLSelectElement) {
      const option = Array.from(control.options).find((o) => o.value === value);
      if (option) control.value = value;
      return;
    }

    if (control instanceof HTMLInputElement && control.type === 'radio') {
      control.checked = control.value === value;
    }
  });
}

function getVariantOptionsArray(variant) {
  if (!variant) return [];
  if (Array.isArray(variant.options)) return variant.options;
  const arr = [variant.option1, variant.option2, variant.option3].filter(Boolean);
  return arr;
}

function getMainVariantOptions(root, productId) {
  const section = root.closest('.shopify-section');
  const vp = section?.querySelector?.(`variant-picker[data-product-id="${productId}"]`);
  const text = vp?.querySelector?.('script[type="application/json"]')?.textContent;
  const parsed = safeJsonParse(text || '');
  return getVariantOptionsArray(parsed);
}

function mergeOptionValues(partial, fallback) {
  const maxLen = Math.max(partial?.length || 0, fallback?.length || 0);
  const merged = Array.from({ length: maxLen });
  for (let i = 0; i < maxLen; i += 1) {
    const v = partial?.[i];
    merged[i] = v != null && v !== '' ? v : fallback?.[i];
  }
  return merged;
}

function initializeDefaultSelections(root, variants, mainVariantOptions) {
  const rows = Array.from(root.querySelectorAll(SELECTORS.row));
  const firstVariant = Array.isArray(variants) ? variants.find((v) => v && v.available !== false) : null;
  const baseValues = mainVariantOptions?.length ? mainVariantOptions : getVariantOptionsArray(firstVariant);
  if (!baseValues?.length) return;

  if (!rows[0]) return;
  setRowOptionValues(rows[0], baseValues);
  // Pre-fill other rows with row #1 values
  rows.slice(1).forEach((row) => setRowOptionValues(row, baseValues));
}

function validateSelections(root, variants, mainVariantOptions) {
  setError(root, '');

  const mode = getMode(root);
  const targetQty = getQty(root, mode);
  const rows = getRowsByMode(root, mode).slice(0, targetQty);
  if (rows.length < targetQty) return { ok: false, ids: [] };

  const ids = [];
  for (const row of rows) {
    if (row.classList.contains('is-collapsed')) return { ok: false, ids: [] };
    const optionValues = mergeOptionValues(getRowOptionValues(row), mainVariantOptions);
    const variantId = findVariantId(variants, optionValues);
    if (!variantId) {
      return { ok: false, ids: [] };
    }
    ids.push(variantId);
  }

  return { ok: true, ids };
}

function copyFirstRowIntoVisibleRows(root, mode) {
  const rows = getRowsByMode(root, mode);
  const firstRow = rows[0];
  if (!firstRow) return;
  const base = getRowOptionValues(firstRow);
  rows.slice(1).forEach((row) => {
    if (!row.classList.contains('is-collapsed')) setRowOptionValues(row, base);
  });
}

async function addItemsToCart({ root, variantIds, productId }) {
  const sections = getCartSectionsToRender();
  const formData = new FormData();

  const normalizedIds = (variantIds || []).map((id) => String(id));
  const allSameVariant =
    normalizedIds.length > 0 && normalizedIds.every((id) => id === normalizedIds[0]);

  if (allSameVariant) {
    // Use the standard add-to-cart payload (id + quantity) to stay compatible with
    // tracking setups that hook into the default product-form/cart-add flow.
    formData.append('id', normalizedIds[0]);
    formData.append('quantity', String(normalizedIds.length));
  } else {
    // Fallback for true multi-variant bundles
    normalizedIds.forEach((id, index) => {
      formData.append(`items[${index}][id]`, id);
      formData.append(`items[${index}][quantity]`, '1');
    });
  }

  if (sections.length) {
    formData.append('sections', sections.join(','));
  }

  const fetchCfg = fetchConfig('javascript', { body: formData });

  const response = await fetch(Theme.routes.cart_add_url, {
    ...fetchCfg,
    headers: {
      ...fetchCfg.headers,
      Accept: 'text/html',
    },
  });

  const json = await response.json();

  if (json?.status) {
    root.dispatchEvent(new CartErrorEvent(root.dataset.bundleOfferBlockId || '', json.message, json.description, json.errors));
    throw new Error(json.message || 'Cart add failed');
  }

  root.dispatchEvent(
    new CartAddEvent({}, root.dataset.bundleOfferBlockId || '', {
      source: 'product-form-component',
      itemCount: variantIds.length,
      productId,
      sections: json.sections,
    })
  );
}

function initOne(root) {
  if (!(root instanceof HTMLElement)) return;
  if (root.dataset.bundleOfferInit === 'true') return;
  root.dataset.bundleOfferInit = 'true';

  const variantsEl = root.querySelector(SELECTORS.variantsJson);
  const variants = safeJsonParse(variantsEl?.textContent || '[]') || [];
  const productId = root.dataset.bundleOfferProductId || '';
  let mainVariantOptions = getMainVariantOptions(root, productId);

  const section = root.closest('.shopify-section');
  section?.addEventListener?.('variant:update', (event) => {
    if (event?.detail?.data?.productId && String(event.detail.data.productId) !== String(productId)) return;
    mainVariantOptions = getVariantOptionsArray(event?.detail?.resource);
  });

  // Default option
  const allowedDefaults = new Set(['single', 'bundle2', 'bundle']);
  let desiredDefault = root.dataset.bundleOfferDefault || 'bundle';
  if (!allowedDefaults.has(desiredDefault)) desiredDefault = 'bundle';
  const hasBundle2 = Array.from(root.querySelectorAll(SELECTORS.optionRadio)).some((r) => r.value === 'bundle2');
  if (desiredDefault === 'bundle2' && !hasBundle2) desiredDefault = 'bundle';
  const radios = Array.from(root.querySelectorAll(SELECTORS.optionRadio));
  radios.forEach((r) => {
    r.checked = r.value === desiredDefault;
  });
  syncSelectedOptionUI(root);

  initializeDefaultSelections(root, variants, mainVariantOptions);
  setModeUI(root, getMode(root));
  copyFirstRowIntoVisibleRows(root, getMode(root));

  root.addEventListener('change', (e) => {
    const target = e.target;
    if (!(target instanceof HTMLElement)) return;

    if (target.matches(SELECTORS.optionRadio)) {
      syncSelectedOptionUI(root);
      const mode = getMode(root);
      setModeUI(root, mode);
      copyFirstRowIntoVisibleRows(root, mode);
      return;
    }

    if (target.matches('select[data-bundle-offer-option]')) {
      // Clear error on any change
      setError(root, '');
    }
  });

  const atcBtn = root.querySelector(SELECTORS.atc);
  atcBtn?.addEventListener('click', async () => {
    setError(root, '');
    if (!(atcBtn instanceof HTMLButtonElement)) return;
    if (root.dataset.bundleOfferAtcInFlight === 'true') return;

    const { ok, ids } = validateSelections(root, variants, mainVariantOptions);
    if (!ok || !ids.length) {
      setError(root, root.dataset.bundleOfferErrorInvalidVariant || 'Please select a valid variant.');
      return;
    }

    atcBtn.disabled = true;
    root.dataset.bundleOfferAtcInFlight = 'true';
    try {
      await addItemsToCart({
        root,
        variantIds: ids,
        productId: root.dataset.bundleOfferProductId,
      });
    } catch (err) {
      setError(root, root.dataset.bundleOfferErrorAddToCartFailed || 'Could not add to cart. Please try again.');
      console.error(err);
    } finally {
      root.dataset.bundleOfferAtcInFlight = 'false';
      atcBtn.disabled = false;
    }
  });
}

function initAll(root = document) {
  root.querySelectorAll(SELECTORS.root).forEach((el) => initOne(el));
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initAll());
} else {
  initAll();
}

document.addEventListener('shopify:section:load', (e) => {
  const target = e?.target;
  if (target instanceof HTMLElement) initAll(target);
});
